import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Icon } from '@/components/ui/icon';
import { AppScreen, IconAction } from '@/components/ui/scene';
import { Text } from '@/components/ui/text';
import { useAuth } from '@/lib/auth/auth';
import { getRouteEntityID, getVenueHref } from '@/lib/navigation/routes';
import { useVenueDetail } from '@/features/venues/useVenueDetail';
import { useVenueOwnerOverview } from '@/features/venues/useVenueOwnerOverview';
import { Redirect, Stack, router, useLocalSearchParams } from 'expo-router';
import {
  ArrowLeftIcon,
  BadgeCheckIcon,
  CalendarPlusIcon,
  ChevronRightIcon,
  EyeIcon,
  StoreIcon,
} from 'lucide-react-native';
import * as React from 'react';
import { ScrollView, View } from 'react-native';

export default function VenueClaimApprovedScreen() {
  const params = useLocalSearchParams<{ id?: string }>();
  const session = useAuth((state) => state.session);
  const venueID = getRouteEntityID(typeof params.id === 'string' ? params.id : '');
  const isOwner = Boolean(session?.me.profile.is_venue && session.me.profile.venue_id === venueID);
  const venueDetailQuery = useVenueDetail(isOwner ? venueID : null);
  const ownerOverviewQuery = useVenueOwnerOverview(venueID, isOwner);
  const detail = venueDetailQuery.data ?? null;
  const ownerOverview = ownerOverviewQuery.data ?? null;
  const venueName = detail?.name ?? session?.me.profile.venue_name ?? 'Your venue';
  const venueHref = React.useMemo(
    () =>
      venueID
        ? getVenueHref(venueID, {}, { slug: detail?.slug, name: venueName })
        : null,
    [detail?.slug, venueID, venueName]
  );
  const hasProfileContent = Boolean(
    detail?.description ||
      detail?.logo_url ||
      detail?.cover_url ||
      detail?.website ||
      detail?.booking_url ||
      detail?.instagram_handle ||
      detail?.phone ||
      (detail?.social_links.length ?? 0) > 0 ||
      (detail?.hours.length ?? 0) > 0 ||
      (detail?.tags.length ?? 0) > 0
  );
  const upcomingEventCount = ownerOverview?.upcoming_events.length ?? 0;

  const handleBack = React.useCallback(() => {
    if (router.canGoBack()) {
      router.back();
      return;
    }

    router.replace((venueHref ?? '/(tabs)/profile') as never);
  }, [venueHref]);

  const handleCompleteProfile = React.useCallback(() => {
    if (!venueHref?.params?.id) {
      return;
    }

    router.replace({
      pathname: '/venues/[id]/edit',
      params: { id: venueHref.params.id },
    });
  }, [venueHref]);

  const handleAddEvent = React.useCallback(() => {
    router.push({
      pathname: '/events/create',
      params: {
        venueId: venueID,
        venueName: detail?.name ?? session?.me.profile.venue_name ?? undefined,
        address: detail?.address || undefined,
        latitude: detail?.lat ? String(detail.lat) : undefined,
        longitude: detail?.lng ? String(detail.lng) : undefined,
      },
    });
  }, [detail?.address, detail?.lat, detail?.lng, detail?.name, session?.me.profile.venue_name, venueID]);

  const handleViewVenuePage = React.useCallback(() => {
    if (!venueHref) {
      return;
    }

    router.replace(venueHref);
  }, [venueHref]);

  if (!session) {
    return <Redirect href={{ pathname: '/sign-in', params: { next: `/venues/${venueID}/approved` } }} />;
  }

  if (!venueID || !isOwner) {
    return <Redirect href={venueHref ? (venueHref as never) : '/(tabs)/profile'} />;
  }

  return (
    <AppScreen>
      <Stack.Screen options={{ headerShown: false }} />

      <ScrollView showsVerticalScrollIndicator={false}>
        <View className="px-5 pb-8 pt-4">
          <View className="mb-4 flex-row items-center">
            <IconAction icon={ArrowLeftIcon} onPress={handleBack} />
          </View>

          <View className="rounded-[28px] border border-primary-edge/35 bg-primary-soft px-5 py-6">
            <View className="flex-row items-center gap-3">
              <View className="size-12 items-center justify-center rounded-full bg-primary">
                <Icon as={BadgeCheckIcon} size={30} className="text-primary-foreground" />
              </View>
              <View className="min-w-0 flex-1 gap-1">
                <Text variant="small" className="uppercase tracking-[2px] text-primary">
                  Venue Approved
                </Text>
                <Text className="text-3xl font-extrabold tracking-tight text-foreground">
                  You&apos;re live
                </Text>
              </View>
            </View>

            <Text className="mt-4 text-base leading-7 text-foreground/85">
              {venueName} is now verified on Radious! Finish the setup, add what&apos;s coming up, and take a look at the page guests will see.
            </Text>
          </View>

          <View className="mt-6 gap-4">
            <OnboardingActionCard
              title={hasProfileContent ? 'Polish your venue profile' : 'Complete your venue profile'}
              description={
                hasProfileContent
                  ? 'Tighten up your logo, cover, hours, links, and details so the page feels ready to share.'
                  : 'Add your logo, cover, hours, links, and key details so people know what your room is about.'
              }
              icon={StoreIcon}
              actionLabel={hasProfileContent ? 'Edit profile' : 'Complete profile'}
              onPress={handleCompleteProfile}
            />

            <OnboardingActionCard
              title={upcomingEventCount > 0 ? 'Keep your calendar fresh' : 'Add your first event'}
              description={
                upcomingEventCount > 0
                  ? `${upcomingEventCount} upcoming ${upcomingEventCount === 1 ? 'event is' : 'events are'} already live. Add the next one while you have momentum.`
                  : 'Post what&apos;s next so guests can discover your venue from the feed right away.'
              }
              icon={CalendarPlusIcon}
              actionLabel={upcomingEventCount > 0 ? 'Add another event' : 'Add event'}
              onPress={handleAddEvent}
            />

            <OnboardingActionCard
              title="See your live venue page"
              description="Open the public page, check the first impression, and make sure everything feels share-ready."
              icon={EyeIcon}
              actionLabel="View venue page"
              onPress={handleViewVenuePage}
            />
          </View>

          <Button className="mt-6 h-12 rounded-xl" onPress={handleViewVenuePage}>
            <Text>View live page</Text>
          </Button>
        </View>
      </ScrollView>
    </AppScreen>
  );
}

function OnboardingActionCard({
  title,
  description,
  icon,
  actionLabel,
  onPress,
}: {
  title: string;
  description: string;
  icon: React.ComponentProps<typeof Icon>['as'];
  actionLabel: string;
  onPress: () => void;
}) {
  return (
    <Card className="border-border bg-card">
      <CardHeader className="flex-row items-start gap-3 space-y-0">
        <View className="mt-0.5 size-10 items-center justify-center rounded-2xl border border-primary/15 bg-primary/10">
          <Icon as={icon} size={18} className="text-primary" />
        </View>
        <View className="min-w-0 flex-1 gap-1">
          <CardTitle className="text-lg text-foreground">{title}</CardTitle>
          <CardDescription className="leading-6 text-muted-foreground">
            {description}
          </CardDescription>
        </View>
      </CardHeader>
      <CardContent>
        <Button
          variant="outline"
          className="h-11 flex-row justify-between rounded-xl border-border bg-background px-4"
          onPress={onPress}>
          <Text className="text-foreground">{actionLabel}</Text>
          <Icon as={ChevronRightIcon} size={16} className="text-muted-foreground" />
        </Button>
      </CardContent>
    </Card>
  );
}
